import { apiFetch } from '../client';
import type { components } from '../generated/backoffice.gen';

// Integraciones M2M (DEC-RBAC-120). Los tipos salen del openapi generado, nunca a mano.
export type Integration = components['schemas']['Integration'];
export type IntegrationKey = components['schemas']['IntegrationKey'];
export type IssuedIntegrationKey = components['schemas']['IssuedIntegrationKey'];
export type CreateIntegrationBody = components['schemas']['CreateIntegrationRequest'];
export type UpdateIntegrationBody = components['schemas']['UpdateIntegrationRequest'];

/** Allowlist vacía o con `*` ⇒ la llave funciona desde cualquier IP (semántica permisiva del backend). */
export function hasNoIpRestriction(integration: Pick<Integration, 'ipAllowlist'>): boolean {
    const allowlist = integration.ipAllowlist;
    return allowlist.length === 0 || allowlist.includes('*');
}

interface RequestOpts {
    idempotencyKey?: string;
}

function withIdempotency(opts: RequestOpts): RequestInit {
    return opts.idempotencyKey ? { headers: { 'Idempotency-Key': opts.idempotencyKey } } : {};
}

const base = (tid: string): string => `/platform/tenants/${tid}/integrations`;

export const integrationsApi = {
    list: (tid: string) => apiFetch<Integration[]>(base(tid)),

    create: (tid: string, body: CreateIntegrationBody, opts: RequestOpts = {}) =>
        apiFetch<Integration>(base(tid), {
            method: 'POST',
            body: JSON.stringify(body),
            ...withIdempotency(opts),
        }),

    suspend: (tid: string, integrationId: string) =>
        apiFetch<Integration>(`${base(tid)}/${integrationId}/suspend`, { method: 'POST' }),

    activate: (tid: string, integrationId: string) =>
        apiFetch<Integration>(`${base(tid)}/${integrationId}/activate`, { method: 'POST' }),

    listKeys: (tid: string, integrationId: string) =>
        apiFetch<IntegrationKey[]>(`${base(tid)}/${integrationId}/keys`),

    // La respuesta trae el secreto en claro: se muestra una vez y no se persiste en ningún lado.
    issueKey: (
        tid: string,
        integrationId: string,
        expiresAt: number | undefined,
        opts: RequestOpts = {},
    ) =>
        apiFetch<IssuedIntegrationKey>(`${base(tid)}/${integrationId}/keys`, {
            method: 'POST',
            body: JSON.stringify(expiresAt !== undefined ? { expiresAt } : {}),
            ...withIdempotency(opts),
        }),

    revokeKey: (tid: string, integrationId: string, keyId: string) =>
        apiFetch<IntegrationKey>(`${base(tid)}/${integrationId}/keys/${keyId}/revoke`, {
            method: 'POST',
        }),
};
